import React, { useState, useMemo } from "react";
import { Check, CheckCheck, AlertCircle, Download, Loader2, Reply } from "lucide-react";
import { formatTimestampForDisplay } from "../../utils/timestampUtils";
import { useTimezone } from "../../contexts/TimezoneContext";
import TemplateMessage from "./TemplateMessage";
import InteractiveMessage, {
  getInteractiveFromMessage,
} from "./InteractiveMessage";
import {
  BubbleTimestampEnd,
  BubbleTimestampOverlay,
  InlineBubbleTimestamp,
} from "./MessageBubbleMeta";
import { getChatMediaSrc } from "../../apis/chat";

// Build a short preview of the replied-to message for the quoted bar.
// Mirrors WhatsApp native: plain text for text / button replies, a
// media-type label for media, and the template body text when available.
const buildReplySnippet = (replyTo) => {
  if (!replyTo) return "";

  // Button / interactive: show just the button label text, no prefix
  if (replyTo.message_type === "button" || replyTo.message_type === "interactive") {
    return replyTo.button_text || replyTo.text || "Button reply";
  }

  // Template: prefer actual body text if available, else just template name
  if (replyTo.message_type === "template") {
    if (replyTo.text) return replyTo.text;
    return replyTo.template_name || "Template message";
  }

  // Media: short type label, optionally followed by caption
  if (["image", "video", "document", "audio", "sticker"].includes(replyTo.message_type)) {
    const labels = { image: "📷 Photo", video: "🎥 Video", audio: "🎵 Audio", document: "📄 Document", sticker: "🎨 Sticker" };
    const base = labels[replyTo.message_type] || "📎 Media";
    return replyTo.text ? `${base} · ${replyTo.text}` : base;
  }

  return replyTo.text || "Message";
};

function isFailedMessage(message) {
  const status = message?.message_status;
  const statuses = message?.statuses || [];
  return status === "failed" || statuses.some((s) => s.status === "failed");
}

const MAX_REPLY_SNIPPET_LENGTH = 120;

const clampReplySnippet = (value) => {
  if (!value) return "";
  return value.length > MAX_REPLY_SNIPPET_LENGTH
    ? `${value.slice(0, MAX_REPLY_SNIPPET_LENGTH)}...`
    : value;
};

/**
 * MessageItem Component - Displays a single message with media support
 */
const MessageItem = ({
  message,
  downloadingMedia,
  onDownloadMedia,
  onStartReply,
  onReplySnippetClick,
  isHighlighted,
  flashQuotedBar,
}) => {
  const [hovered, setHovered] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  // Subscribing here re-renders the bubble when the user changes the account
  // timezone, so existing open chats reflect the new zone without a reload.
  const { timezone: activeTimezone } = useTimezone();

  const mediaSrc = useMemo(
    () => getChatMediaSrc(message, message.phone_number_id),
    [message]
  );

  /**
   * Get message status icon
   * Status progression: sent (1 grey tick) -> delivered (2 grey ticks) -> read (2 blue ticks)
   * Failed shows red icon with error message
   */
  const getStatusIcon = (message) => {
    if (message.direction === "inbound") return null;
    
    const status = message.message_status || "sent";
    const statuses = message.statuses || [];
    
    // Check for failed status (highest priority)
    const hasFailed = status === "failed" || statuses.some((s) => s.status === "failed");
    const errorMessage = message.error_details || message.error_message || message.error_title || message.error_code;

    if (hasFailed) {
      return (
        <div className="relative group">
          <AlertCircle size={12} className="text-red-500" />
          {errorMessage && (
            <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
              {errorMessage}
              {message.error_code && (
                <div className="text-[10px] text-gray-400 mt-1">
                  Code: {message.error_code}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    // Check for read status (two blue ticks)
    const hasRead = status === "read" || statuses.some((s) => s.status === "read");
    if (hasRead) {
      return <CheckCheck size={12} className="text-[#53bdeb]" />;
    }
    
    // Check for delivered status (two grey ticks)
    const hasDelivered = status === "delivered" || statuses.some((s) => s.status === "delivered");
    if (hasDelivered) {
      return <CheckCheck size={12} className="text-gray-400" />;
    }
    
    // Default: sent status (one grey tick)
    if (status === "sent") {
      return <Check size={12} className="text-gray-400" />;
    }
    
    return null;
  };

  /**
   * Handle media load completion (called from onLoad/onError handlers)
   * Clears the downloading state when media finishes loading
   */
  const handleMediaLoadComplete = () => {
    if (onDownloadMedia) {
      onDownloadMedia(message, true);
    }
  };

  /**
   * Render media content based on message type
   */
  const renderMedia = () => {
    if (!message.media_url && !message.media_id) return null;
    if (message.message_type === "text" || message.message_type === "button") return null;

    const isDownloading = downloadingMedia.has(message.message_id);
    const hasDownloadedUrl = !!message.downloaded_media_url;
    const displaySrc = mediaSrc || message.downloaded_media_url;

    // Image messages
    if (message.message_type === "image" || message.mime_type?.startsWith("image/")) {
      if (hasDownloadedUrl && displaySrc) {
        return (
          <div className="relative">
            <img
              key={`img-${message.message_id}-${displaySrc}`}
              src={displaySrc}
              alt="Image"
              onClick={() =>
                setPreviewMedia({
                  type: "image",
                  url: displaySrc,
                  title: message.caption || message.message_text || "Image preview",
                })
              }
              className="max-w-full max-h-64 rounded object-cover w-full cursor-zoom-in"
              onLoad={handleMediaLoadComplete}
              onError={(e) => {
                console.error("Error loading downloaded image:", e);
                e.target.style.display = "none";
                if (onDownloadMedia) onDownloadMedia(message, true);
              }}
            />
            {isDownloading && (
              <div className="absolute inset-0 bg-black/30 rounded flex items-center justify-center z-10">
                <div className="bg-white/90 px-3 py-2 rounded-lg shadow-lg flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-primary" />
                  <span className="text-xs text-gray-700">Processing...</span>
                </div>
              </div>
            )}
          </div>
        );
      } else {
        return (
          <div className="relative bg-gray-200 rounded flex items-center justify-center min-h-[200px]">
            {!hasDownloadedUrl && (
              <div className="absolute inset-0 bg-black/40 rounded flex items-center justify-center z-10 group-hover:bg-black/50 transition-colors">
                <button
                  onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
                  disabled={isDownloading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg font-medium transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${
                    message.direction === "outbound"
                      ? "bg-white text-primary hover:bg-gray-50"
                      : "bg-primary text-white hover:bg-primary/90"
                  }`}
                  title={message.media_id && !message.media_url ? "Download media from Meta" : "Download media to view"}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      <span className="text-sm">Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      <span className="text-sm">Download to View</span>
                    </>
                  )}
                </button>
              </div>
            )}
            <div className="text-center">
              <div className="text-4xl mb-2">📷</div>
              <p className="text-xs text-gray-500">
                {message.media_id && !message.media_url 
                  ? "Click to download from Meta" 
                  : "Image preview unavailable"}
              </p>
            </div>
          </div>
        );
      }
    }

    // Video messages
    if (message.message_type === "video" || message.mime_type?.startsWith("video/")) {
      if (hasDownloadedUrl && displaySrc) {
        return (
          <video
            key={displaySrc}
            src={displaySrc}
            controls
            onClick={() =>
              setPreviewMedia({
                type: "video",
                url: displaySrc,
                title: message.caption || message.message_text || "Video preview",
              })
            }
            className="max-w-full max-h-64 rounded w-full cursor-zoom-in"
            onLoadedData={handleMediaLoadComplete}
            onError={(e) => {
              console.error("Error loading downloaded video:", e);
              if (onDownloadMedia) onDownloadMedia(message, true);
            }}
          >
            Your browser does not support the video tag.
          </video>
        );
      } else {
        return (
          <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">🎥</div>
            <p className="text-sm font-medium text-gray-700 mb-3">Video Available</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        );
      }
    }

    // Audio messages
    if (message.message_type === "audio" || message.mime_type?.startsWith("audio/")) {
      if (hasDownloadedUrl || message.media_url) {
        const audioSrc = displaySrc || message.media_url;
        return (
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-2xl">🎵</span>
            <audio 
              key={audioSrc}
              src={audioSrc} 
              controls 
              className="flex-1" 
            />
          </div>
        );
      } else {
        return (
          <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg text-center border-2 border-dashed border-gray-300">
            <div className="text-4xl mb-2">🎵</div>
            <p className="text-sm font-medium text-gray-700 mb-3">Audio Available</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        );
      }
    }

    // Document messages
    if (message.message_type === "document" || message.mime_type) {
      return (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <span className="text-3xl">📄</span>
          <div className="flex-1">
            {hasDownloadedUrl && displaySrc ? (
              <a
                href={displaySrc}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  setPreviewMedia({
                    type: "document",
                    url: displaySrc,
                    title: message.message_text || "Document preview",
                  });
                }}
                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline break-all block"
              >
                {message.message_text || "Document"}
              </a>
            ) : (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">{message.message_text || "Document"}</p>
                <button
                  onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
                  disabled={isDownloading}
                  className="px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  {isDownloading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Downloading...</span>
                    </>
                  ) : (
                    <>
                      <Download size={14} />
                      <span>Download</span>
                    </>
                  )}
                </button>
              </div>
            )}
            {message.file_size && (
              <p className="text-xs text-gray-500 mt-1">
                {(message.file_size / 1024).toFixed(2)} KB
              </p>
            )}
          </div>
        </div>
      );
    }

    // Generic media fallback
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        {hasDownloadedUrl && displaySrc ? (
          <a
            href={displaySrc}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
          >
            <span className="text-xl">📎</span>
            <span>View Media</span>
          </a>
        ) : (
          <div className="text-center">
            <p className="text-sm font-medium text-gray-700 mb-3">Media file</p>
            <button
              onClick={() => onDownloadMedia && onDownloadMedia(message, false)}
              disabled={isDownloading}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 mx-auto"
            >
              {isDownloading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <Download size={16} />
                  <span>Download to View</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const isOutbound = message.direction === "outbound";
  const canReply = onStartReply && !isFailedMessage(message);
  const replyTo = message.reply_to || null;
  const replySnippet = replyTo ? clampReplySnippet(buildReplySnippet(replyTo)) : "";

  const interactivePayload =
    message.message_type === "interactive"
      ? getInteractiveFromMessage(message)
      : null;

  const isCarouselInteractive =
    interactivePayload &&
    (interactivePayload.type || "").toLowerCase() === "carousel";

  const displayTime = formatTimestampForDisplay(message.sent_at, activeTimezone);
  const statusIcon = isOutbound ? getStatusIcon(message) : null;

  const embedTimestampInRichContent =
    message.message_type === "template" ||
    (message.message_type === "interactive" &&
      message.direction === "outbound" &&
      interactivePayload);

  const isPlainText =
    message.message_type === "text" && !message.caption;
  const hasCaption =
    message.caption &&
    ["image", "video", "audio", "document"].includes(message.message_type);
  const isButtonReply =
    message.message_type === "button" ||
    (message.message_type === "interactive" &&
      (message.direction === "inbound" || !interactivePayload));

  const timestampMeta = embedTimestampInRichContent ? (
    <BubbleTimestampOverlay time={displayTime} statusIcon={statusIcon} />
  ) : null;

  const inlineTimestamp = (
    <InlineBubbleTimestamp time={displayTime} statusIcon={statusIcon} />
  );

  const showBottomTimestamp =
    !embedTimestampInRichContent &&
    !isPlainText &&
    !hasCaption &&
    !isButtonReply;

  return (
    <div
      className={`flex group mi-msg-row min-w-0 w-full max-w-full ${
        isOutbound ? "justify-end" : "justify-start"
      }`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Hover "reply" action — rendered outside the bubble so it doesn't
          eat bubble padding. Positioned on the side away from the bubble
          edge to feel native to the conversation side. */}
      {isOutbound && canReply && (
        <button
          type="button"
          onClick={() => onStartReply(message)}
          className={`mi-reply-edge self-center mr-0.5 sm:mr-1 p-1.5 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors shrink-0 ${
            hovered ? "opacity-100" : "opacity-90 sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto"
          }`}
          title="Reply"
          aria-label="Reply to message"
        >
          <Reply size={14} />
        </button>
      )}

      <div
        className={`relative min-w-0 overflow-hidden px-2.5 pt-2 pb-1.5 sm:px-3 sm:py-2 transition-shadow [overflow-wrap:anywhere] ${
          isCarouselInteractive
            ? "w-full max-w-full"
            : "inline-block max-w-[min(88%,420px)] sm:max-w-[min(68%,480px)]"
        } ${
          isOutbound
            ? "rounded-tr-sm rounded-tl-[9px] rounded-bl-[9px] rounded-br-[9px] shadow-[0_1px_0.5px_rgba(17,27,33,0.13)] bg-[#d9fdd3] text-[#111b21]"
            : "rounded-tl-sm rounded-tr-[9px] rounded-bl-[9px] rounded-br-[9px] shadow-[0_1px_0.5px_rgba(17,27,33,0.13)] bg-white text-[#111b21]"
        } ${isHighlighted ? "ring-2 ring-yellow-400/80 ring-offset-2 ring-offset-transparent" : ""}`}
      >
        {/* Quoted reply bar — shown when this message is a reply to another. */}
        {replyTo && (
          <button
            type="button"
            onClick={() => onReplySnippetClick && onReplySnippetClick(replyTo.message_id)}
            className={`w-full text-left mb-2 flex items-stretch rounded overflow-hidden transition-all ${
              isOutbound ? "bg-[rgba(17,27,33,0.06)] hover:bg-[rgba(17,27,33,0.09)]" : "bg-gray-50 hover:bg-gray-100"
            } ${flashQuotedBar ? "ring-2 ring-yellow-400/80" : ""}`}
            title="Jump to original message"
          >
            <div className={`w-1 flex-shrink-0 ${isOutbound ? "bg-[#25D366]" : "bg-emerald-600"}`} />
            <div className="px-2 py-1 flex-1 min-w-0">
              <p className={`text-[11px] font-semibold leading-tight truncate ${
                isOutbound ? "text-[#059669]" : "text-emerald-700"
              }`}>
                {replyTo.sender_label || (replyTo.direction === "inbound" ? "Customer" : "You")}
              </p>
              <p className={`text-[11px] leading-tight truncate break-all overflow-hidden ${
                isOutbound ? "text-[#54656f]" : "text-gray-600"
              }`}>
                {replySnippet}
              </p>
            </div>
          </button>
        )}

        {/* Template Messages */}
        {message.message_type === "template" && (
          <TemplateMessage
            message={message}
            isOutbound={message.direction === "outbound"}
            onPreview={setPreviewMedia}
            metaFooter={timestampMeta}
          />
        )}

        {message.message_type === "interactive" &&
          message.direction === "outbound" &&
          interactivePayload && (
          <InteractiveMessage
            message={message}
            isOutbound={true}
            onPreview={setPreviewMedia}
            metaFooter={timestampMeta}
          />
        )}

        {(message.message_type === "button" ||
          (message.message_type === "interactive" &&
            (message.direction === "inbound" ||
              !getInteractiveFromMessage(message)))) && (
          <div className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            {message.button?.text || message.button?.payload || message.message_text || "Button reply"}
            {inlineTimestamp}
          </div>
        )}
        
        {/* Media Messages */}
        {message.message_type !== "template" && (message.media_url || (message.media_id && message.message_type !== "text" && message.message_type !== "button" && message.message_type !== "interactive")) && (
          <div className="mb-2 relative group">
            {renderMedia()}
          </div>
        )}

        {/* Caption for media messages */}
        {hasCaption && (
          <div className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] mt-2">
            {message.caption}
            {inlineTimestamp}
          </div>
        )}

        {/* Message Text (text messages only, not template/button/interactive-with-payload) */}
        {isPlainText && message.message_text && (
          <div className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            {message.message_text}
            {inlineTimestamp}
          </div>
        )}

        {showBottomTimestamp && (
          <BubbleTimestampEnd time={displayTime} statusIcon={statusIcon} />
        )}
      </div>
      {previewMedia && (
        <MediaPreviewModal
          media={previewMedia}
          onClose={() => setPreviewMedia(null)}
        />
      )}

      {/* Hover reply action for inbound messages (shown after the bubble). */}
      {!isOutbound && canReply && (
        <button
          type="button"
          onClick={() => onStartReply(message)}
          className={`mi-reply-edge self-center ml-0.5 sm:ml-1 p-1.5 rounded-full bg-white shadow border border-gray-200 text-gray-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors shrink-0 ${
            hovered ? "opacity-100" : "opacity-90 sm:opacity-0 sm:pointer-events-none sm:group-hover:opacity-100 sm:group-hover:pointer-events-auto"
          }`}
          title="Reply"
          aria-label="Reply to message"
        >
          <Reply size={14} />
        </button>
      )}
    </div>
  );
};

function MediaPreviewModal({ media, onClose }) {
  const isImage = media?.type === "image";
  const isVideo = media?.type === "video";
  const isDocument = media?.type === "document";
  if (!media?.url) return null;

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <p className="text-sm font-medium text-gray-800 truncate">{media.title || "Preview"}</p>
          <button type="button" onClick={onClose} className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200">
            Close
          </button>
        </div>
        <div className="p-3 bg-black/5 max-h-[calc(90vh-48px)] overflow-auto">
          {isImage && (
            <img src={media.url} alt={media.title || "Preview"} className="w-full h-auto max-h-[78vh] object-contain rounded" />
          )}
          {isVideo && (
            <video src={media.url} controls className="w-full max-h-[78vh] rounded bg-black" />
          )}
          {isDocument && (
            <div className="space-y-2">
              <iframe title={media.title || "Document preview"} src={media.url} className="w-full h-[70vh] rounded border border-gray-200 bg-white" />
              <a href={media.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline">
                Open document in new tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MessageItem;

